/**
 * User Preference Service
 * Handles loading and applying user preferences (language, theme, etc.) on login
 * Now uses PreferenceCoordinator for robust conflict-free operation
 * Enhanced with complete preference management via API
 */

import { AppDispatch } from '../store';
import { preferenceCoordinator } from './preferenceCoordinator';
import { userPreferencesApi } from './api/userPreferencesApi';
import { UserPreferences as FullUserPreferences } from '../types/auth';
import { logger } from '../utils/logger';
import { UserPreferences, PreferenceLoadResult } from '@/app/types/domain/preferences';

/**
 * User Preference Service Class
 * Now delegates to PreferenceCoordinator for robust operation
 */
export class UserPreferenceService {
  /**
   * Load and apply all user preferences on login
   * Robust solution using centralized coordination
   */
  async loadAndApplyUserPreferences(
    userId: number, 
    dispatch: AppDispatch,
    options: {
      loadLanguage?: boolean;
      loadTheme?: boolean;
      applyImmediately?: boolean;
      forceReload?: boolean;
    } = {}
  ): Promise<PreferenceLoadResult> {
    const {
      loadLanguage = true,
      loadTheme = true,
      
      forceReload = false
    } = options;

    logger.preferenceLoad('UserPreferenceService: Loading preferences for user', userId, true, options);

    try {
      // Use the centralized coordinator for robust preference loading
      const preferenceState = await preferenceCoordinator.loadUserPreferences(
        userId,
        dispatch,
        {
          loadLanguage,
          loadTheme,
          forceReload
        }
      );

      // Convert coordinator state to our interface
      const preferences: UserPreferences = {};
      const errors: string[] = [];

      if (preferenceState.language) {
        preferences.language = preferenceState.language;
      }

      if (preferenceState.themeMode && preferenceState.colorTheme) {
        preferences.theme = {
          mode: preferenceState.themeMode,
          colorTheme: preferenceState.colorTheme
        };
      }

      // Check if application was successful
      const success = preferenceState.isApplied;
      if (!success) {
        errors.push('Preferences loaded but not applied');
      }

      const result: PreferenceLoadResult = {
        success,
        preferences,
        error: errors.length > 0 ? errors.join(', ') : undefined
      };

      logger.preferenceLoad('UserPreferenceService: Preference loading completed', userId, true, result);
      return result;

    } catch (error) {
      logger.error('UserPreferenceService: Error loading preferences', error, { userId });
      
      return {
        success: false,
        preferences: {},
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check if preferences are currently loading for a user
   */
  isLoadingPreferences(userId: number): boolean {
    return preferenceCoordinator.isLoadingPreferences(userId);
  }

  /**
   * Get current preferences for a user
   */
  getUserPreferences(userId: number): UserPreferences {
    const state = preferenceCoordinator.getUserPreferences(userId);
    if (!state) {
      return {};
    }

    const preferences: UserPreferences = {};
    
    if (state.language) {
      preferences.language = state.language;
    }

    if (state.themeMode && state.colorTheme) {
      preferences.theme = {
        mode: state.themeMode,
        colorTheme: state.colorTheme
      };
    }

    return preferences;
  }

  /**
   * Clear preferences for a user (useful for logout)
   */
  clearUserPreferences(userId: number): void {
    preferenceCoordinator.clearUserPreferences(userId);
  }

  /**
   * Clear all preferences (useful for cleanup)
   */
  clearAllPreferences(): void {
    preferenceCoordinator.clearAllPreferences();
  }

  /**
   * Migrate guest preferences to user account (called after login)
   */
  async migrateGuestPreferences(userId: number, dispatch: AppDispatch): Promise<void> {
    try {
      logger.info('UserPreferenceService: Migrating guest preferences for user', { userId });

      // Migrate language preference
      const { migrateGuestLanguagePreference } = await import('../features/language/utils/initializeLanguage');
      await migrateGuestLanguagePreference(dispatch, userId);

      // TODO: Add theme preference migration if needed
      // const guestTheme = getGuestThemePreference();
      // if (guestTheme) {
      //   await themePreferenceService.syncThemeToServer(guestTheme, userId);
      // }

      logger.info('UserPreferenceService: Guest preferences migrated successfully');
    } catch (error) {
      logger.error('UserPreferenceService: Failed to migrate guest preferences', error, { userId });
      // Don't throw - this is not critical for login flow
    }
  }

  /**
   * Register a system to be notified of preference changes
   * This allows other systems to coordinate with preference loading
   */
  registerSystem(systemName: string, callback: (event: any) => void): () => void {
    return preferenceCoordinator.registerSystem(systemName, callback);
  }

  /**
   * Get coordination status (for debugging)
   */
  getStatus(): {
    loadedUsers: number[];
    loadingUsers: number[];
    queueLength: number;
    isProcessingQueue: boolean;
  } {
    return preferenceCoordinator.getStatus();
  }

  /**
   * Get full user preferences from API
   * Returns complete preference object with all fields
   */
  async getFullUserPreferences(userId: number): Promise<FullUserPreferences> {
    try {
      logger.info('UserPreferenceService: Fetching full preferences for user', { userId });
      const preferences = await userPreferencesApi.getUserPreferences(userId);
      logger.info('UserPreferenceService: Full preferences fetched successfully', { userId });
      return preferences;
    } catch (error) {
      logger.error('UserPreferenceService: Failed to fetch full preferences', error, { userId });
      throw error;
    }
  }

  /**
   * Update a single preference field
   * Type-safe method that ensures only valid preference keys can be updated
   */
  async updatePreference<K extends keyof FullUserPreferences>(
    userId: number,
    key: K,
    value: FullUserPreferences[K]
  ): Promise<void> {
    try {
      logger.info('UserPreferenceService: Updating preference', { userId, key, value });
      
      const updates: Partial<FullUserPreferences> = {
        [key]: value
      } as Partial<FullUserPreferences>;
      
      await userPreferencesApi.updateUserPreferences(userId, updates);
      logger.info('UserPreferenceService: Preference updated successfully', { userId, key });
    } catch (error) {
      logger.error('UserPreferenceService: Failed to update preference', error, { userId, key });
      throw error;
    }
  }

  /**
   * Update multiple preferences at once
   * More efficient than updating one at a time
   */
  async updateMultiplePreferences(
    userId: number,
    updates: Partial<FullUserPreferences>
  ): Promise<FullUserPreferences> {
    try {
      logger.info('UserPreferenceService: Updating multiple preferences', { userId, updates });
      const result = await userPreferencesApi.updateUserPreferences(userId, updates);
      logger.info('UserPreferenceService: Multiple preferences updated successfully', { userId });
      return result;
    } catch (error) {
      logger.error('UserPreferenceService: Failed to update multiple preferences', error, { userId });
      throw error;
    }
  }

  /**
   * Reset user preferences to system defaults
   * Fetches defaults from configuration and applies them
   */
  async resetToDefaults(userId: number): Promise<FullUserPreferences> {
    try {
      logger.info('UserPreferenceService: Resetting preferences to defaults', { userId });
      
      // Get default preferences from system configuration
      const defaults = await userPreferencesApi.getDefaultPreferences();
      
      // Apply defaults to user preferences
      const result = await userPreferencesApi.updateUserPreferences(userId, defaults);
      
      logger.info('UserPreferenceService: Preferences reset to defaults successfully', { userId });
      return result;
    } catch (error) {
      logger.error('UserPreferenceService: Failed to reset preferences to defaults', error, { userId });
      throw error;
    }
  }

  /**
   * Get default preferences from system configuration
   * Useful for showing users what the defaults are
   */
  async getDefaultPreferences(): Promise<Partial<FullUserPreferences>> {
    try {
      logger.info('UserPreferenceService: Fetching default preferences');
      const defaults = await userPreferencesApi.getDefaultPreferences();
      logger.info('UserPreferenceService: Default preferences fetched successfully');
      return defaults;
    } catch (error) {
      logger.error('UserPreferenceService: Failed to fetch default preferences', error);
      throw error;
    }
  }
}

// Export singleton instance
export const userPreferenceService = new UserPreferenceService();